import { Button } from "@/components/ui/button";
import { FictionalHome, fmt } from "@/hooks/useHomeBuyingSim";
import { ArrowLeft, Check } from "lucide-react";
import homeStarter from "@/assets/home-starter.png";
import homeFamily from "@/assets/home-family.png";
import homeSuburban from "@/assets/home-suburban.png";

const HOME_IMAGES: Record<string, string> = {
  starter: homeStarter,
  family: homeFamily,
  suburban: homeSuburban,
  starter_home: homeStarter,
  family_home: homeFamily,
  larger_suburban_home: homeSuburban,
};

interface Props {
  homes: FictionalHome[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

export default function HomeSelection({ homes, selectedKey, onSelect, onContinue, onBack }: Props) {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Choose a Home</h2>
        <p className="text-sm text-muted-foreground">Each home has different costs and commitments. Select the one you want to explore.</p>
      </div>
      <div className="space-y-3">
        {homes.map(h => {
          const isSelected = selectedKey === h.home_key;
          return (
          <button
            key={h.home_key}
            onClick={() => onSelect(h.home_key)}
            aria-pressed={isSelected}
            className={`relative w-full text-left rounded-2xl overflow-hidden transition-all duration-200 ${
              isSelected
                ? 'glass-card border-2 border-primary ring-4 ring-primary/40 shadow-[0_0_30px_hsl(var(--primary)/0.45)] scale-[1.01]'
                : 'glass-card border border-border/60 hover:border-primary/50 hover:shadow-card-hover opacity-90 hover:opacity-100'
            }`}
          >
            {isSelected && (
              <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[10px] font-heading font-bold uppercase tracking-wider shadow-lg">
                <Check className="w-3 h-3" /> Selected
              </span>
            )}
            {HOME_IMAGES[h.home_key] && (
              <div className="aspect-video w-full overflow-hidden">
                <img
                  src={HOME_IMAGES[h.home_key]}
                  alt={h.home_name}
                  className={`w-full h-full object-cover transition-transform duration-300 ${isSelected ? 'scale-105' : ''}`}
                />
              </div>
            )}
            <div className="p-5 space-y-1">
              <div className="flex items-center justify-between">
                <p className={`font-heading font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>{h.home_name}</p>
              </div>
              <p className="text-lg font-bold text-foreground">{fmt(h.home_price)}</p>
              <p className="text-xs text-muted-foreground">{h.description}</p>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
                <span>Tax: {fmt(h.property_tax_monthly)}/mo</span>
                <span>Insurance: {fmt(h.insurance_monthly)}/mo</span>
                <span className="capitalize">Maintenance: {h.maintenance_rating}</span>
              </div>
            </div>
          </button>
          );
        })}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Button onClick={onContinue} disabled={!selectedKey} className="flex-1">Continue</Button>
      </div>
    </div>
  );
}
